import { useMemo } from 'react'
import type { ModelsData, ModelGroups } from '../types'

interface Category {
  id: string
  title: string
  description: string
  image: string
  groups: string[]
}

interface CategoryGridProps {
  modelsData: ModelsData
  modelGroups: ModelGroups
  onCategorySelect: (category: Category) => void
}

const CategoryGrid = ({ modelsData, onCategorySelect }: CategoryGridProps) => {
  
  const categories = useMemo(() => {
    // Group related categories and get first model image for each
    const getFirstModelImage = (groupKeys: string[]): string => {
      for (const groupKey of groupKeys) {
        const models = modelsData[groupKey]
        if (models) {
          const firstModelKey = Object.keys(models)[0]
          if (firstModelKey && models[firstModelKey]?.image) {
            return models[firstModelKey].image
          }
        }
      }
      return 'images/unico-logo.png'
    }

    return [
      {
        id: 'dragon-proste',
        title: 'Dragon Proste',
        description: 'Wkłady powietrzne Dragon z prostą szybą',
        image: getFirstModelImage(['dragon-proste', 'dragon-proste-aku']),
        groups: ['dragon-proste', 'dragon-proste-aku']
      },
      {
        id: 'dragon-narozne',
        title: 'Dragon Narożne',
        description: 'Wkłady narożne Dragon do zabudowy w rogu',
        image: getFirstModelImage(['dragon-narozne', 'dragon-narozne-aku']),
        groups: ['dragon-narozne', 'dragon-narozne-aku']
      },
      {
        id: 'dragon-vis-a-vis',
        title: 'Dragon Vis-a-vis',
        description: 'Wkłady dwustronne Dragon z szybą vis-a-vis',
        image: getFirstModelImage(['dragon-vis-a-vis', 'dragon-vis-a-vis-aku']),
        groups: ['dragon-vis-a-vis', 'dragon-vis-a-vis-aku']
      },
      {
        id: 'dragon-portal',
        title: 'Dragon Portal',
        description: 'Wkłady Dragon z portalem do zabudowy',
        image: getFirstModelImage(['dragon-portal']),
        groups: ['dragon-portal']
      },
      {
        id: 'dragon-trojstronne',
        title: 'Dragon Trójstronne',
        description: 'Trójstronne przeszklenie zapewnia wizję ognia w palenisku o bardzo dużym spektrum.',
        image: getFirstModelImage(['dragon-trojstronne']),
        groups: ['dragon-trojstronne']
      },
      {
        id: 'dragon-polokragle',
        title: 'Dragon Półokrągłe',
        description: 'Wkłady Dragon z półokrągłą szybą',
        image: getFirstModelImage(['dragon-polokragle']),
        groups: ['dragon-polokragle']
      },
      {
        id: 'nemo-proste',
        title: 'Nemo Proste',
        description: 'Wkłady wodne Nemo z prostą szybą',
        image: getFirstModelImage(['nemo-proste']),
        groups: ['nemo-proste']
      },
      {
        id: 'nemo-narozne',
        title: 'Nemo Narożne',
        description: 'Wkłady narożne Nemo do zabudowy w rogu',
        image: getFirstModelImage(['nemo-narozne']),
        groups: ['nemo-narozne']
      },
      {
        id: 'nemo-vis-a-vis',
        title: 'Nemo Vis-a-vis',
        description: 'Wkłady dwustronne Nemo z szybą vis-a-vis',
        image: getFirstModelImage(['nemo-vis-a-vis']),
        groups: ['nemo-vis-a-vis']
      }
    ]
  }, [modelsData])

  return (
    <div className="model-grid-container">
      <div className="model-grid">
        {categories.map((category) => (
          <div
            key={category.id}
            className="model-tile"
            onClick={() => onCategorySelect(category)}
          >
            <div className="model-image">
              <img 
                src={category.image} 
                alt={category.title}
                onError={(e) => {
                  e.currentTarget.src = 'images/unico-logo.png'
                }}
              />
              <div className="model-hover-description">
                <div className="model-specs-hover">
                  {category.description}
                </div>
              </div>
            </div>
            <div className="model-label">
              <div className="model-label-header">
                <h4>{category.title}</h4>
                <button 
                  className="model-info-icon"
                  onClick={(e) => {
                    e.stopPropagation()
                    const tile = e.currentTarget.closest('.model-tile')
                    if (tile) {
                      tile.classList.toggle('show-description')
                    }
                  }}
                  title="Zobacz opis"
                >
                  i
                </button>
              </div>
              <div className="model-power">Kategoria wkładów</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default CategoryGrid
export type { Category }